import React from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import brandLogo from '../../assets/images/logo.png';
import './app-redirect.scss';

const SECTION_CONFIG = {
  feed: {
    title: 'Your Feed',
    subtitle: 'Discover videos, posts, and stories from teachers and learners worldwide.',
    icon: 'bi-play-circle-fill',
    emoji: '📱',
    scheme: 'yoospeak://feed',
    color: '#2193F2',
    features: [
      { icon: 'bi-camera-video', text: 'Watch language learning videos' },
      { icon: 'bi-heart', text: 'Like and save your favourites' },
      { icon: 'bi-chat-dots', text: 'Comment and interact with the community' },
    ],
  },
  practice: {
    title: 'Practice Zone',
    subtitle: 'Sharpen your speaking and listening skills with interactive practice sessions.',
    icon: 'bi-mic-fill',
    emoji: '🎙️',
    scheme: 'yoospeak://practice',
    color: '#7c3aed',
    features: [
      { icon: 'bi-mic', text: 'Real-time pronunciation practice' },
      { icon: 'bi-lightning', text: 'Vocabulary and grammar drills' },
      { icon: 'bi-trophy', text: 'Track your daily streak and progress' },
    ],
  },
  friends: {
    title: 'Friends & Connections',
    subtitle: 'Connect with fellow learners, share progress, and practice together.',
    icon: 'bi-people-fill',
    emoji: '🤝',
    scheme: 'yoospeak://friends',
    color: '#059669',
    features: [
      { icon: 'bi-person-plus', text: 'Find and follow language partners' },
      { icon: 'bi-chat-heart', text: 'Chat and practice in real time' },
      { icon: 'bi-trophy', text: 'Compete on leaderboards with friends' },
    ],
  },
};

const handleOpenApp = (scheme) => {
  const ua = navigator.userAgent;
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isChrome = /Chrome\//i.test(ua) && !/Edg\/|OPR\//i.test(ua);

  const section = scheme.replace('yoospeak://', '');
  const androidIntent = `intent://${section}#Intent;scheme=yoospeak;package=com.yoospeak.learning;launchFlags=268435456;end`;

  if (isAndroid && isChrome) {
    window.location.href = androidIntent;
  } else if (isAndroid || isIOS) {
    window.location.href = scheme;
  } else {
    // Desktop — show store links (already visible)
    const storeSection = document.getElementById('store-links');
    if (storeSection) {
      storeSection.scrollIntoView({ behavior: 'smooth' });
      storeSection.classList.add('store-pulse');
      setTimeout(() => storeSection.classList.remove('store-pulse'), 1500);
    }
  }
};

export const AppRedirect = ({ section }) => {
  const config = SECTION_CONFIG[section] || SECTION_CONFIG.feed;

  return (
    <div className="app-redirect-page">
      {/* Animated background */}
      <div className="bg-gradient-anim" style={{ '--accent': config.color }}></div>

      <Container className="position-relative">
        <div className="redirect-wrapper mx-auto text-center py-5">
          {/* Logo */}
          <div className="logo-wrap mb-5">
            <img src={brandLogo} alt="Yoospeak" className="img-fluid" style={{ height: '64px' }} />
          </div>

          {/* Section Icon */}
          <div className="section-icon-wrap mb-4">
            <div className="section-icon-circle" style={{ '--accent': config.color }}>
              <span className="emoji">{config.emoji}</span>
            </div>
          </div>

          {/* Heading */}
          <h1 className="redirect-title fw-black text-white mb-3">
            {config.title}
          </h1>
          <p className="redirect-subtitle text-white text-opacity-80 mb-5 mx-auto" style={{ maxWidth: '480px' }}>
            {config.subtitle}
          </p>

          {/* Features list */}
          <div className="features-list mx-auto mb-5" style={{ maxWidth: '400px' }}>
            {config.features.map((f, i) => (
              <div key={i} className="feature-item d-flex align-items-center gap-3 mb-3">
                <div className="feature-icon-wrap" style={{ '--accent': config.color }}>
                  <i className={`bi ${f.icon}`}></i>
                </div>
                <span className="text-white fw-medium">{f.text}</span>
              </div>
            ))}
          </div>

          {/* Open in App CTA */}
          <button
            className="btn-open-app mb-4"
            style={{ '--accent': config.color }}
            onClick={() => handleOpenApp(config.scheme)}
          >
            <i className="bi bi-phone-fill me-2 fs-5"></i>
            Open in Yoospeak App
          </button>

          {/* App Store Links */}
          <div id="store-links">
            <p className="text-white text-opacity-60 text-sm mb-3">Don't have the app yet?</p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <Link
                to="https://play.google.com/store/apps/details?id=com.yoospeak.learning"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={require('../../assets/images/play-store.png')}
                  alt="Google Play Store"
                  className="img-fluid rounded-3 store-badge"
                  style={{ width: '155px' }}
                />
              </Link>
              <Link
                to="https://apps.apple.com/in/app/yoospeak/id6746839854"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={require('../../assets/images/app-store.png')}
                  alt="App Store"
                  className="img-fluid rounded-3 store-badge"
                  style={{ width: '155px' }}
                />
              </Link>
            </div>
          </div>

          {/* Back link */}
          <div className="mt-5">
            <Link to="/" className="text-white text-opacity-60 text-sm text-decoration-none">
              <i className="bi bi-arrow-left me-1"></i> Back to Yoospeak Website
            </Link>
          </div>
        </div>
      </Container>
    </div>
  );
};
